import React, { useEffect, useState } from "react";
import {
  Box,
  ButtonBase,
  Chip,
  Container,
  Fab,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  Dashboard as DashboardIcon,
  WhatsApp as WhatsAppIcon,
  ReceiptLong as ReceiptIcon,
  Inbox as InboxIcon,
  Inventory as InventoryIcon,
  TrendingUp as TrendingIcon,
  ChevronRight as ChevronRightIcon,
  HelpOutline as HelpIcon,
} from "@mui/icons-material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Button from "@mui/material/Button";
import { AnimatePresence, motion } from "framer-motion";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import QRCode from "react-qr-code";

// Custom hooks
import useCostsData from "../../hooks/DeliveryCenterCosts/useCostsData";
import useCharts from "../../hooks/DeliveryCenterCosts/useCharts";
import useAlert from "../../hooks/DeliveryCenterCosts/useAlert";

// Components
import CostsPage from "./CostsPage";
import CostsDashboard from "./CostsDashboard";
import CategoryManagement from "./CategoryManagement";
import SupplierManagement from "./SupplierManagement";
import FiscalDocumentsList from "./FiscalDocumentsList";
import CustomAlert from "../ui/CustomAlert";
import TutorialModal, { isTutorialDismissed } from "./modals/TutorialModal";

const ACCENT = "#059669";
const ACCENT_LIGHT = "#10b981";
const ACCENT_DARK = "#047857";
const GRADIENT =
  "linear-gradient(145deg, #064e3b 0%, #047857 30%, #059669 65%, #10b981 100%)";

const _rawBotLink = process.env.REACT_APP_WHATSAPP_BOT_LINK;
const WHATSAPP_BOT_LINK = _rawBotLink && _rawBotLink !== "null" ? _rawBotLink : "";

const NAV_ITEMS = [
  {
    icon: MoneyIcon,
    label: "Notas Fiscais",
    desc: "Lancar e gerenciar",
  },
  {
    icon: InboxIcon,
    label: "Notas Recebidas",
    desc: "Auditar envios do bot",
  },
  {
    icon: CategoryIcon,
    label: "Categorias",
    desc: "Tipos de custo",
  },
  {
    icon: BusinessIcon,
    label: "Fornecedores",
    desc: "Parceiros comerciais",
  },
  {
    icon: DashboardIcon,
    label: "Dashboard",
    desc: "Visao analitica",
  },
];

const features = [
  { icon: ReceiptIcon, label: "Notas fiscais" },
  { icon: InventoryIcon, label: "Categorias e fornecedores" },
  { icon: TrendingIcon, label: "Dashboard analitico" },
  { icon: WhatsAppIcon, label: "Bot WhatsApp" },
];

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.05 },
  },
};

const riseUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 200, damping: 22 },
  },
};

const contentFade = {
  initial: { opacity: 0, x: 12 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.25, ease: "easeOut" } },
  exit: { opacity: 0, x: -12, transition: { duration: 0.15 } },
};

const CostsIndexPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);
  const [tutorialOpen, setTutorialOpen] = useState(() => !isTutorialDismissed());

  const {
    costs,
    categories,
    suppliers,
    categorySummary,
    weeklySummary,
    totalCosts,
    averageCost,
    loading,
    error,
    fetchAllData,
    getCategoryName,
  } = useCostsData();

  const {
    activeTab: chartActiveTab,
    chartType,
    CHART_COLORS,
    handleTabChange: handleChartTabChange,
    handleChartTypeChange,
    getCategoryChartData,
    getWeeklyChartData,
    renderCustomizedPieLabel,
  } = useCharts(getCategoryName);

  const { alertOpen, alertMessage, alertSeverity, showAlert, closeAlert } =
    useAlert();

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (error) showAlert(error, "error");
  }, [error]);

  const scrollToContent = () => {
    document
      .getElementById("costs-content")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return <CostsPage />;
      case 1:
        return <FiscalDocumentsList />;
      case 2:
        return <CategoryManagement />;
      case 3:
        return <SupplierManagement />;
      case 4:
        return (
          <CostsDashboard
            totalCosts={totalCosts}
            categories={categories}
            suppliers={suppliers}
            averageCost={averageCost}
            loading={loading}
            categorySummary={categorySummary}
            weeklySummary={weeklySummary}
            chartType={chartType}
            activeTab={chartActiveTab}
            chartColors={CHART_COLORS}
            renderCustomizedPieLabel={renderCustomizedPieLabel}
            handleTabChange={handleChartTabChange}
            handleChartTypeChange={handleChartTypeChange}
            getCategoryChartData={getCategoryChartData}
            getWeeklyChartData={getWeeklyChartData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {/* ─── HERO ─── */}
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            pt: { xs: 6, md: 10 },
            pb: { xs: 8, md: 14 },
            background: GRADIENT,
          }}
        >
          {/* Grid pattern */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0.04,
              backgroundImage:
                "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />

          {/* Glow */}
          <Box
            component={motion.div}
            animate={{ y: [0, -12, 0] }}
            transition={{
              duration: 10,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            sx={{
              position: "absolute",
              width: 420,
              height: 420,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${alpha("#34d399", 0.12)} 0%, transparent 65%)`,
              top: -140,
              right: -80,
            }}
          />

          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
            <motion.div variants={stagger} initial="hidden" animate="visible">
              {/* Badge */}
              <motion.div variants={riseUp}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ mb: 2.5 }}
                >
                  <Chip
                    label="CUSTO SIMPLES"
                    size="small"
                    sx={{
                      backgroundColor: alpha("#fff", 0.15),
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "0.65rem",
                      letterSpacing: "2px",
                      backdropFilter: "blur(8px)",
                      border: `1px solid ${alpha("#fff", 0.2)}`,
                    }}
                  />
                </Stack>
              </motion.div>

              {/* Title */}
              <motion.div variants={riseUp}>
                <Typography
                  variant={isMobile ? "h4" : "h2"}
                  fontWeight={800}
                  align="center"
                  sx={{
                    color: "#fff",
                    mb: 2,
                    letterSpacing: "-0.02em",
                    lineHeight: 1.15,
                  }}
                >
                  Controle financeiro
                  <br />
                  <Box
                    component="span"
                    sx={{
                      background: "linear-gradient(90deg, #34d399, #a7f3d0)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    descomplicado
                  </Box>
                </Typography>
              </motion.div>

              <motion.div variants={riseUp}>
                <Typography
                  variant="h6"
                  align="center"
                  fontWeight={400}
                  sx={{
                    color: alpha("#fff", 0.65),
                    maxWidth: 520,
                    mx: "auto",
                    mb: 5,
                    lineHeight: 1.6,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                  }}
                >
                  Gerencie custos, categorias e fornecedores em um so lugar.
                  Lance notas, acompanhe dashboards e use o bot do WhatsApp.
                </Typography>
              </motion.div>

              {/* Feature pills */}
              <motion.div variants={stagger}>
                <Grid
                  container
                  spacing={2}
                  justifyContent="center"
                  sx={{ mb: 4 }}
                >
                  {features.map((f, i) => {
                    const Icon = f.icon;
                    return (
                      <Grid item key={i}>
                        <motion.div variants={riseUp}>
                          <Chip
                            icon={
                              <Icon
                                sx={{
                                  fontSize: 16,
                                  color: `${alpha("#34d399", 0.9)} !important`,
                                }}
                              />
                            }
                            label={f.label}
                            sx={{
                              backgroundColor: alpha("#fff", 0.08),
                              color: "#fff",
                              fontWeight: 500,
                              fontSize: "0.78rem",
                              border: `1px solid ${alpha("#fff", 0.12)}`,
                              backdropFilter: "blur(4px)",
                              px: 0.5,
                              height: 34,
                            }}
                          />
                        </motion.div>
                      </Grid>
                    );
                  })}
                </Grid>
              </motion.div>

              {/* Scroll CTA */}
              <motion.div variants={riseUp}>
                <Stack alignItems="center">
                  <Button
                    onClick={scrollToContent}
                    endIcon={<ArrowDownwardIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      color: alpha("#fff", 0.6),
                      textTransform: "none",
                      fontWeight: 500,
                      "&:hover": {
                        color: "#fff",
                        backgroundColor: alpha("#fff", 0.06),
                      },
                    }}
                  >
                    Ver custos
                  </Button>
                </Stack>
              </motion.div>
            </motion.div>
          </Container>

          {/* Separator */}
          <Box
            sx={{
              position: "absolute",
              bottom: -1,
              left: 0,
              right: 0,
              lineHeight: 0,
            }}
          >
            <svg
              viewBox="0 0 1440 80"
              preserveAspectRatio="none"
              style={{ width: "100%", height: 50, display: "block" }}
            >
              <path
                d="M0 80L1440 0V80H0Z"
                fill={theme.palette.background.default}
              />
            </svg>
          </Box>
        </Box>

        {/* ─── CONTENT ─── */}
        <Box
          id="costs-content"
          sx={{
            maxWidth: 1440,
            mx: "auto",
            py: { xs: 2, md: 5 },
            px: { xs: 1.5, md: 4, lg: 6 },
          }}
        >
          <CustomAlert
            open={alertOpen}
            message={alertMessage}
            severity={alertSeverity}
            onClose={closeAlert}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 2, md: 3 },
              alignItems: "flex-start",
            }}
          >
            {/* ─── SIDEBAR NAV (desktop) / PILL BAR (mobile) ─── */}
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.4 }}
              sx={{
                // Desktop: fixed-width sidebar
                width: { xs: "100%", md: 240 },
                flexShrink: 0,
                position: { md: "sticky" },
                top: { md: 24 },
              }}
            >
              {/* ── WhatsApp Bot QR Code Panel (desktop only) ── */}
              {!isMd && !!WHATSAPP_BOT_LINK && (
                <Paper
                  elevation={0}
                  component={motion.div}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15, duration: 0.35 }}
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${alpha(ACCENT, 0.2)}`,
                    overflow: "hidden",
                    background: `linear-gradient(145deg, ${alpha(ACCENT_DARK, 0.04)} 0%, ${alpha(ACCENT, 0.06)} 100%)`,
                    mb: 2,
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1.25,
                      borderBottom: `1px solid ${alpha(ACCENT, 0.15)}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      background: `linear-gradient(135deg, ${ACCENT_DARK} 0%, ${ACCENT} 100%)`,
                    }}
                  >
                    <WhatsAppIcon sx={{ fontSize: 15, color: "#fff" }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.65rem",
                        letterSpacing: "1.2px",
                        color: "#fff",
                        textTransform: "uppercase",
                      }}
                    >
                      Bot de leitura
                    </Typography>
                  </Box>

                  {/* Body */}
                  <Box sx={{ px: 2, py: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                    {/* QR Code */}
                    <Box
                      component="a"
                      href={WHATSAPP_BOT_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: "block",
                        p: 1.25,
                        borderRadius: 2,
                        background: "#fff",
                        border: `2px solid ${alpha(ACCENT, 0.2)}`,
                        cursor: "pointer",
                        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          borderColor: ACCENT,
                          boxShadow: `0 0 0 3px ${alpha(ACCENT, 0.15)}`,
                        },
                      }}
                    >
                      <QRCode
                        value={WHATSAPP_BOT_LINK}
                        size={120}
                        level="M"
                        fgColor={ACCENT_DARK}
                      />
                    </Box>

                    {/* Explanation */}
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          color: ACCENT_DARK,
                          mb: 0.5,
                          lineHeight: 1.3,
                        }}
                      >
                        Envie suas notas de compra
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.66rem",
                          color: "text.secondary",
                          lineHeight: 1.4,
                          display: "block",
                        }}
                      >
                        Aponte a camera para o QR Code e envie fotos das notas fiscais para o WhatsApp do sistema. A IA faz a leitura automaticamente.
                      </Typography>
                    </Box>

                    {/* Tap hint */}
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.6rem",
                        color: alpha(ACCENT, 0.7),
                        fontStyle: "italic",
                      }}
                    >
                      Clique no QR Code para abrir
                    </Typography>
                  </Box>
                </Paper>
              )}

              {isMd ? (
                /* ── Mobile: horizontal scrollable pills ── */
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    overflowX: "auto",
                    pb: 1,
                    px: 0.5,
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  {NAV_ITEMS.map((item, idx) => {
                    const Icon = item.icon;
                    const isActive = activeTab === idx;
                    return (
                      <ButtonBase
                        key={idx}
                        onClick={() => setActiveTab(idx)}
                        sx={{
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          fontSize: "0.8rem",
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? "#fff" : theme.palette.text.secondary,
                          background: isActive
                            ? `linear-gradient(135deg, ${ACCENT_DARK} 0%, ${ACCENT} 100%)`
                            : alpha(theme.palette.grey[200], 0.6),
                          boxShadow: isActive
                            ? `0 2px 8px ${alpha(ACCENT, 0.3)}`
                            : "none",
                          transition: "all 0.2s ease",
                          whiteSpace: "nowrap",
                          "&:hover": {
                            background: isActive
                              ? `linear-gradient(135deg, ${ACCENT_DARK} 0%, ${ACCENT} 100%)`
                              : alpha(theme.palette.grey[200], 1),
                          },
                        }}
                      >
                        <Icon sx={{ fontSize: 18 }} />
                        {item.label}
                      </ButtonBase>
                    );
                  })}
                </Box>
              ) : (
                /* ── Desktop: vertical sidebar navigation ── */
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    overflow: "hidden",
                    background: theme.palette.background.paper,
                  }}
                >
                  {/* Sidebar header */}
                  <Box
                    sx={{
                      px: 2.5,
                      py: 2,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      background: alpha(ACCENT, 0.03),
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.65rem",
                        letterSpacing: "1.5px",
                        color: theme.palette.text.disabled,
                        textTransform: "uppercase",
                      }}
                    >
                      Navegacao
                    </Typography>
                  </Box>

                  {/* Nav items */}
                  <Box sx={{ py: 0.5 }}>
                    {NAV_ITEMS.map((item, idx) => {
                      const Icon = item.icon;
                      const isActive = activeTab === idx;
                      return (
                        <ButtonBase
                          key={idx}
                          onClick={() => setActiveTab(idx)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            textAlign: "left",
                            px: 2,
                            py: 1.5,
                            mx: 0.5,
                            my: 0.25,
                            borderRadius: 2,
                            position: "relative",
                            transition: "all 0.2s ease",
                            background: isActive
                              ? alpha(ACCENT, 0.08)
                              : "transparent",
                            "&:hover": {
                              background: isActive
                                ? alpha(ACCENT, 0.1)
                                : alpha(theme.palette.grey[500], 0.06),
                            },
                            // Active left accent bar
                            "&::before": isActive
                              ? {
                                  content: '""',
                                  position: "absolute",
                                  left: 0,
                                  top: "20%",
                                  bottom: "20%",
                                  width: 3,
                                  borderRadius: 4,
                                  background: `linear-gradient(180deg, ${ACCENT} 0%, ${ACCENT_LIGHT} 100%)`,
                                }
                              : {},
                          }}
                        >
                          {/* Icon container */}
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mr: 1.5,
                              flexShrink: 0,
                              background: isActive
                                ? `linear-gradient(135deg, ${ACCENT_DARK} 0%, ${ACCENT} 100%)`
                                : alpha(theme.palette.grey[400], 0.1),
                              transition: "all 0.2s ease",
                            }}
                          >
                            <Icon
                              sx={{
                                fontSize: 18,
                                color: isActive
                                  ? "#fff"
                                  : theme.palette.text.disabled,
                                transition: "color 0.2s ease",
                              }}
                            />
                          </Box>

                          {/* Label + description */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: isActive ? 600 : 500,
                                fontSize: "0.82rem",
                                color: isActive
                                  ? ACCENT_DARK
                                  : theme.palette.text.primary,
                                lineHeight: 1.3,
                                transition: "color 0.2s ease",
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.68rem",
                                color: isActive
                                  ? alpha(ACCENT, 0.7)
                                  : theme.palette.text.disabled,
                                lineHeight: 1.2,
                              }}
                            >
                              {item.desc}
                            </Typography>
                          </Box>

                          {/* Chevron */}
                          <ChevronRightIcon
                            sx={{
                              fontSize: 16,
                              color: isActive
                                ? ACCENT
                                : alpha(theme.palette.text.disabled, 0.4),
                              ml: 0.5,
                              transition: "all 0.2s ease",
                              transform: isActive
                                ? "translateX(2px)"
                                : "translateX(0)",
                            }}
                          />
                        </ButtonBase>
                      );
                    })}
                  </Box>

                  {/* Sidebar footer with quick stats */}
                  <Box
                    sx={{
                      px: 2.5,
                      py: 2,
                      mt: 0.5,
                      borderTop: `1px solid ${theme.palette.divider}`,
                      background: alpha(theme.palette.grey[100], 0.5),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.65rem",
                          color: theme.palette.text.disabled,
                        }}
                      >
                        Registros
                      </Typography>
                      <Chip
                        label={loading ? "..." : costs.length}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          backgroundColor: alpha(ACCENT, 0.1),
                          color: ACCENT,
                        }}
                      />
                    </Box>
                  </Box>
                </Paper>
              )}
            </Box>

            {/* ─── MAIN CONTENT AREA ─── */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 500,
              }}
            >
              <Paper
                elevation={0}
                component={motion.div}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: "hidden",
                  background: theme.palette.background.paper,
                }}
              >
                {/* Content header bar */}
                <Box
                  sx={{
                    px: { xs: 2, md: 3 },
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    background: alpha(theme.palette.grey[50], 0.8),
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_LIGHT} 100%)`,
                        boxShadow: `0 0 6px ${alpha(ACCENT, 0.4)}`,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        color: theme.palette.text.primary,
                      }}
                    >
                      {NAV_ITEMS[activeTab]?.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.7rem",
                        color: theme.palette.text.disabled,
                        display: { xs: "none", sm: "block" },
                      }}
                    >
                      {NAV_ITEMS[activeTab]?.desc}
                    </Typography>
                  </Box>
                </Box>

                {/* Tab content with animation */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    variants={contentFade}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                  >
                    {renderTabContent()}
                  </motion.div>
                </AnimatePresence>
              </Paper>
            </Box>
          </Box>
        </Box>

        {/* ─── TUTORIAL MODAL ─── */}
        <TutorialModal
          open={tutorialOpen}
          onClose={() => setTutorialOpen(false)}
          onNavigateToTab={setActiveTab}
        />

        {/* ─── FLOATING HELP BUTTON ─── */}
        <Tooltip title="Como usar o Custo Simples" placement="left">
          <Fab
            size="medium"
            onClick={() => setTutorialOpen(true)}
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              zIndex: 1000,
              bgcolor: ACCENT,
              color: "#fff",
              boxShadow: `0 4px 14px ${alpha(ACCENT, 0.4)}`,
              "&:hover": {
                bgcolor: ACCENT_DARK,
              },
            }}
          >
            <HelpIcon />
          </Fab>
        </Tooltip>
      </motion.div>
    </LocalizationProvider>
  );
};

export default CostsIndexPage;
